import { useCallback, useContext, useEffect, useMemo } from 'react';
import { X } from 'react-feather';
import Select from 'react-select';
import styled from '@emotion/styled';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTheme } from '@mui/material';
import { buildSelectStyles, buildSelectTheme } from '@trinity/chat-components/components/Chat.Filters/Presentation.js';
import { camelToSpaceCase } from '@trinity/chat-components/utils';
import { KolSearchResultsFilterContext } from './context';
import type { KolSearchTableFilters } from './interfaces.state';
import type { FilterType, KolSearchResultSortKey } from './interfaces.types';
import { SORT_KEYS, doesFilterTypeRequireValue, getValidFilterTypesForKey, getDefaultTypeForKey } from './utils';

export const FilterPanel = () => {
  const { state, dispatch, setOpen } = useContext(KolSearchResultsFilterContext);

  useEffect(() => {
    if (!state.length) {
      setOpen(false);
    }
  }, [dispatch, setOpen, state]);

  return (
    <FiltersContainer>
      {state.map(f => <FilterEntry key={f.identifier} filter={f} />)}
      {state.length >= 3 && (
        <FilterLimitMessage>
          Maximum of 3 filters allowed
        </FilterLimitMessage>
      )}
    </FiltersContainer>
  );
};

type FilterEntryProps = {
  filter: KolSearchTableFilters.State['filters'][number];
};
const FilterEntry = ({ filter }: FilterEntryProps) => {
  const { state, dispatch } = useContext(KolSearchResultsFilterContext);
  const theme = useTheme();
  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);
  const filterTypes = useMemo(() => buildSelectOptions(getValidFilterTypesForKey(filter.key)), [filter.key]);

  const onKeyChange = useCallback((key: KolSearchResultSortKey) => {
    dispatch({
      type: 'filter-updated',
      payload: {
        identifier: filter.identifier,
        key,
        type: getDefaultTypeForKey(key),
        value: '',
      },
    });
  }, [dispatch, filter.identifier]);

  const onOperatorChange = useCallback((operator: FilterType) => {
    dispatch({
      type: 'filter-updated',
      payload: {
        identifier: filter.identifier,
        type: operator,
        value: doesFilterTypeRequireValue(operator) ? filter.value : '',
      },
    });
  }, [dispatch, filter.identifier, filter.value]);

  const onValueChange = useCallback((value: string) => {
    dispatch({
      type: 'filter-updated',
      payload: {
        identifier: filter.identifier,
        value,
      },
    });
  }, [dispatch, filter.identifier]);

  const validateDistanceValue = useCallback((value: string): string => {
    if (value === '') return '';

    const numericValue = value.replace(/[^\d.]/g, '');

    if (numericValue === '') return '';

    const parts = numericValue.split('.');
    let cleanValue = parts[0];
    if (parts.length > 1) {
      cleanValue += '.' + parts.slice(1).join('');
    }

    if (cleanValue.startsWith('.')) {
      cleanValue = '0' + cleanValue;
    }

    const num = parseFloat(cleanValue);
    if (!isNaN(num)) {
      if (num < 0) return '0';
      if (num > 1000) return '1000';
    }

    return cleanValue;
  }, []);

  const onDistanceValueChange = useCallback((value: string) => {
    const validatedValue = validateDistanceValue(value);
    dispatch({
      type: 'filter-updated',
      payload: {
        identifier: filter.identifier,
        value: validatedValue,
      },
    });
  }, [dispatch, filter.identifier, validateDistanceValue]);

  const onDistanceKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([8, 9, 27, 13, 46, 190, 110].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  }, []);

  const onRemoveFilter = useCallback(() => {
    dispatch({
      type: 'filter-removed',
      payload: { identifier: filter.identifier },
    });
  }, [dispatch, filter.identifier]);

  return (
    <FilterRow>
      <SelectDiv>
        <Select<Option<KolSearchResultSortKey>>
          options={COLUMN_OPTIONS}
          isSearchable={false}
          value={COLUMN_OPTIONS.find(x => x.value === filter.key)}
          placeholder={'Column'}
          getOptionLabel={o => camelToSpaceCase(o.label)}
          onChange={o => onKeyChange(o.value)}
          styles={selectStyles}
          theme={selectTheme} />
      </SelectDiv>
      <SelectDiv>
        <Select<Option<FilterType>>
          options={filterTypes}
          isSearchable={false}
          value={filterTypes.find(x => x.value === filter.type)}
          placeholder={'Operator'}
          getOptionLabel={o => o.label.replaceAll('-', ' ')}
          onChange={o => onOperatorChange(o.value)}
          styles={selectStyles}
          theme={selectTheme} />
      </SelectDiv>
      {filter.key === 'isMun' ?
        <SelectDiv>
          <Select<Option<string>>
            options={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]}
            isSearchable={false}
            isDisabled={!doesFilterTypeRequireValue(filter.type)}
            value={filter.value ? { label: filter.value === 'true' ? 'Yes' : 'No', value: filter.value } : null}
            placeholder={'Filter Value'}
            getOptionLabel={o => o.label}
            onChange={o => onValueChange(o.value)}
            styles={selectStyles}
            theme={selectTheme} />
        </SelectDiv>
        : filter.key === 'distance' && filter.type === 'is within' ?
          <InputWithUnit>
            <InputWithUnitField
              type="text"
              inputMode="decimal"
              onChange={e => onDistanceValueChange(e.target.value)}
              onKeyDown={onDistanceKeyDown}
              value={filter.value ?? ''}
              placeholder='0-1000'
              disabled={!doesFilterTypeRequireValue(filter.type)}
              min="0"
              max="1000"
            />
            <UnitLabel>miles</UnitLabel>
          </InputWithUnit>
          :
          <Input
            onChange={e => onValueChange(e.target.value)}
            value={filter.value ?? ''}
            placeholder='Filter Value'
            disabled={!doesFilterTypeRequireValue(filter.type)}
          />
      }
      <RemoveFilterIcon onClick={onRemoveFilter} />
      {state.length < 3 && (
        <AddFilterIcon onClick={() => dispatch({ type: 'filter-added', payload: { key: 'name' } })} />
      )}
    </FilterRow>
  );
};

const FiltersContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const FilterLimitMessage = styled.div(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.gray.main,
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '5px 0',
}));

const FilterRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const SelectDiv = styled.div({
  width: 175,
});

const Input = styled.input(({ theme }) => ({
  fontSize: 16,
  padding: 8,
  width: 250,
  height: 38,
  borderRadius: 5,
  border: `1px solid ${theme.palette.gray.light1} `,
  fontFamily: theme.fonts.regular,

  '&:focus': {
    borderColor: theme.isTrinityThemed ? theme.palette.blue.main : theme.palette.primary.main,
    borderWidth: 2,
  },

  '&::placeholder': {
    // color: theme.palette.gray.light1,
    fontFamily: theme.fonts.regular,
    fontSize: 14,
  },
}));

const InputWithUnitField = styled.input(({ theme }) => ({
  fontSize: 16,
  padding: 8,
  paddingRight: 50, // Make room for the "miles" label
  width: '100%',
  height: 38,
  borderRadius: 5,
  border: `1px solid ${theme.palette.gray.light1} `,
  fontFamily: theme.fonts.regular,

  '&:focus': {
    borderColor: theme.isTrinityThemed ? theme.palette.blue.main : theme.palette.primary.main,
    borderWidth: 2,
  },

  '&::placeholder': {
    color: theme.palette.gray.main,
    fontFamily: theme.fonts.regular,
    fontSize: 14,
  },
}));

const RemoveFilterIcon = styled(X)(({ theme }) => ({
  cursor: 'pointer',
  padding: 6,
  height: 28,
  width: 28,
  borderRadius: 20,
  flexShrink: 0,
  flexGrow: 0,
  '&:hover': {
    backgroundColor: theme.palette.gray.light4,
  },
}));

const AddFilterIcon = styled(AddCircleIcon)(({ theme }) => ({
  cursor: 'pointer',
  padding: 6,
  height: 28,
  width: 28,
  color: theme.palette.primary.main,
  borderRadius: 20,
  flexShrink: 0,
  flexGrow: 0,
  '&:hover': {
    backgroundColor: theme.palette.gray.light4,
  },
}));

const InputWithUnit = styled.div({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  width: 250,
});

const UnitLabel = styled.span(({ theme }) => ({
  position: 'absolute',
  right: 12,
  fontSize: 14,
  color: theme.palette.gray.main,
  fontFamily: theme.fonts.regular,
  pointerEvents: 'none',
}));

function buildSelectOptions<T extends string>(options: T[]): Option<T>[] {
  return options.map(o => ({ label: o, value: o }));
}

const COLUMN_OPTIONS = buildSelectOptions(SORT_KEYS);

type Option<T extends string> = {
  label: T;
  value: T;
};